/**
 * @fileoverview Hook that provides primary navigation links configuration and responsive menu handling
 * @module features/primaryNavigation/routes/usePrimaryNavLinks
 */
import React from 'react';
import { PrimaryNavLink } from 'basecamp';
import { useResponsiveMenu, useWindowSize } from '../../../utils/hooks';
import { translate } from '../../../utils/locale';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { getFeature } from 'features/app/redux/features';
import { withinEmbargoAndViewAsSchool } from '../../../utils/embargoHelpers';
import {
	getEmbargo,
	getSubscriptionStatus,
	getViewAsSchool,
	getActiveChildSchool,
	getActiveKs4ChildSchool,
} from 'features/app/redux/context';
import { getAllGroupTrends } from 'features/groups/analysis/redux/selectors';

/**
 * Custom hook that manages primary navigation links and responsive menu behaviour
 * @returns {Object} Object containing large and small screen menu configurations
 * @returns {Array} largeScreenMenu - Array of menu items for large screen display
 * @returns {Array} smallScreenMenu - Array of menu items for small screen display
 */
const useSummitPrimaryNavLinks = () => {
	const embargoFeature = useSelector(getFeature('embargo_feature'));
	const subscriptionStatus = useSelector(getSubscriptionStatus);
	const viewAsSchool = useSelector(getViewAsSchool);
	const trends = useSelector(getAllGroupTrends);
	const canViewSummitKs5Pm = useSelector(getFeature('summit_ks5'));
	// US 52996: the KS5 PM titles only take their new wording once the expanded area is on.
	const canViewExpandedKs5Pm = useSelector(getFeature('expanded_summit_ks5_performance_measures'));
	const canViewSummitKs4Pm = useSelector(getFeature('summit_ks4_pm'));
	const canViewSummitKs4PmGroupOverview = useSelector(getFeature('summit_ks4_pm_group_overview'));
	const applyEmbargo = useSelector(getEmbargo);
	const embargoAccess = withinEmbargoAndViewAsSchool(embargoFeature, applyEmbargo, viewAsSchool);
	// Check if this is a returnUrl redirect - if so, bypass subscription check for link disabling
	const isReturnUrlRedirect =
		typeof window !== 'undefined' && window.sessionStorage.getItem('returnUrlRedirect') === 'true';
	const commonDisabledLinkCriteria =
		(!isReturnUrlRedirect && !subscriptionStatus.hasValidSubscriptionOrContract) ||
		!trends ||
		embargoAccess;
	const activeChildSchool = useSelector(getActiveChildSchool);
	const activeKs4ChildSchool = useSelector(getActiveKs4ChildSchool);
	const history = useHistory();
	const { pathname } = useLocation();
	const isOnKs4PmPage = pathname.startsWith('/groups/ks4-pm');

	/**
	 * Configuration array for all primary navigation links
	 * Each object contains name, width, visibility, menu order and component details
	 */
	const summitPrimaryNavLinks = [
		{
			name: 'strategicAnalysis',
			width: 104,
			isVisible: true,
			menuTransferOrder: 1,
			component: (
				<PrimaryNavLink
					setAs="primary"
					data-test={`primary-nav-summit-strategic-analysis-link`}
					withInternalUrl="/groups/strategic"
					disabled={commonDisabledLinkCriteria}
				>
					{translate('primaryNavigation.summit.root.STRATEGIC')}
				</PrimaryNavLink>
			),
		},
		{
			name: 'bbpaAnalysis',
			width: 124,
			isVisible: true,
			menuTransferOrder: 2,
			component: (
				<PrimaryNavLink
					setAs="primary"
					data-test={`primary-nav-summit-bbpa-link`}
					withInternalUrl="/groups/bbpa"
					disabled={commonDisabledLinkCriteria}
				>
					{translate('primaryNavigation.summit.root.BBPA')}
				</PrimaryNavLink>
			),
		},
		{
			name: 'subjectsAnalysis',
			width: 175,
			isVisible: true,
			menuTransferOrder: 3,
			component: (
				<PrimaryNavLink
					setAs="primary"
					data-test={`primary-nav-summit-subjects-analysis-link`}
					withInternalUrl="/groups/subjects"
					disabled={commonDisabledLinkCriteria}
				>
					{translate('primaryNavigation.summit.root.SUBJECTS_ANALYSIS')}
				</PrimaryNavLink>
			),
		},
		{
			name: 'groupOverview',
			// The 'KS5 Performance Measures: ...' title is far longer than the one it
			// replaces, so the width useResponsiveMenu measures has to follow the flag too.
			width: canViewExpandedKs5Pm ? 300 : 175,
			isVisible: canViewSummitKs5Pm,
			menuTransferOrder: 4,
			component: (
				<PrimaryNavLink
					setAs="primary"
					data-test={`primary-nav-summit-overview-of-providers-in-group-link`}
					withInternalUrl="/groups/ks5pm/group-overview"
					disabled={!canViewSummitKs5Pm || commonDisabledLinkCriteria}
				>
					{translate(
						canViewExpandedKs5Pm
							? 'primaryNavigation.summit.root.OVERVIEW_OF_PROVIDERS_IN_GROUP_NEW'
							: 'primaryNavigation.summit.root.OVERVIEW_OF_PROVIDERS_IN_GROUP'
					)}
				</PrimaryNavLink>
			),
		},
		{
			name: 'providerLevelSummary',
			width: canViewExpandedKs5Pm ? 300 : 165,
			isVisible: canViewSummitKs5Pm,
			menuTransferOrder: 5,
			component: (
				<PrimaryNavLink
					setAs="primary"
					data-test={`primary-nav-summit-provider-level-summary-link`}
					// Temp fix: when on a KS4 page, withInternalUrl is stripped so PrimaryNavLink's
					// last-segment active detection returns false (avoids dual-highlight with KS4 link
					// when both sections share the same school name). Navigation is handled via onClick.
					// TODO: remove once PrimaryNavLink isLinkSelected is fixed in basecamp for /groups/ routes.
					withInternalUrl={
						isOnKs4PmPage ? undefined : `/groups/ks5pm/${activeChildSchool?.childSchoolName || ''}`
					}
					onClick={
						isOnKs4PmPage
							? (e: React.MouseEvent) => {
									e.preventDefault();
									history.push(`/groups/ks5pm/${activeChildSchool?.childSchoolName || ''}`);
							  }
							: undefined
					}
					disabled={
						!canViewSummitKs5Pm || commonDisabledLinkCriteria || !activeChildSchool?.childSchoolName
					}
				>
					{translate(
						canViewExpandedKs5Pm
							? 'primaryNavigation.summit.root.PROVIDER_LEVEL_SUMMARY_NEW'
							: 'primaryNavigation.summit.root.PROVIDER_LEVEL_SUMMARY'
					)}
				</PrimaryNavLink>
			),
		},
		{
			name: 'ks4GroupOverview',
			width: 175,
			isVisible: canViewSummitKs4PmGroupOverview,
			menuTransferOrder: 6,
			component: (
				<PrimaryNavLink
					setAs="primary"
					data-test={`primary-nav-summit-ks4-pm-group-overview-link`}
					withInternalUrl="/groups/ks4-pm-group-overview"
					disabled={!canViewSummitKs4PmGroupOverview || commonDisabledLinkCriteria}
				>
					{translate('primaryNavigation.summit.root.KS4_GROUP_OVERVIEW')}
				</PrimaryNavLink>
			),
		},
		{
			name: 'ks4PmSummary',
			width: 165,
			isVisible: canViewSummitKs4Pm,
			menuTransferOrder: 7,
			component: (
				<PrimaryNavLink
					setAs="primary"
					data-test={`primary-nav-summit-ks4-pm-summary-link`}
					withInternalUrl={
						activeKs4ChildSchool?.childSchoolName
							? `/groups/ks4-pm/${activeKs4ChildSchool.childSchoolName}`
							: '/groups/ks4-pm'
					}
					disabled={!canViewSummitKs4Pm || commonDisabledLinkCriteria}
				>
					{translate('primaryNavigation.summit.root.KS4_PM_SUMMARY')}
				</PrimaryNavLink>
			),
		},
	];

	/**
	 * Get the current window width to determine responsive menu breakpoints
	 * @type {number} width - Current window width in pixels
	 */
	const { width } = useWindowSize();

	// Generate responsive menus based on screen size and dependencies
	// Uses primaryNavLinks array to create large and small screen menu variants
	// Dependencies array ensures menu updates when relevant data changes
	const { largeScreenMenu, smallScreenMenu } = useResponsiveMenu(
		summitPrimaryNavLinks,
		width,
		100,
		[pathname, activeKs4ChildSchool?.childSchoolId, activeChildSchool?.childSchoolId]
	);

	return { largeScreenMenu, smallScreenMenu };
};

export default useSummitPrimaryNavLinks;
